import React, { useState, useEffect, useCallback } from 'react';
import api from '../../api';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const BulkUploadModal = ({
  isOpen,
  onClose,
  tenantId,
  department,
  createdBy,
  refreshData,
  currentAcademicContext = null
}) => {
  const [fileQueue, setFileQueue] = useState([]);
  const [, setUploadJobs] = useState(new Map());
  const [isUploading, setIsUploading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [uploadStage, setUploadStage] = useState('');
  const [academicContext, setAcademicContext] = useState(null);
  const [availableContexts, setAvailableContexts] = useState([]);
  const [loadingContexts, setLoadingContexts] = useState(false);
  const [uploadResults, setUploadResults] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [expandedFiles, setExpandedFiles] = useState(new Set());

  const notify = (message, type = 'info') => {
    try {
      if (type === 'error') {
        toast.error(String(message), { autoClose: 6000 });
      } else if (type === 'warning') {
        toast.warn(String(message), { autoClose: 5000 });
      } else if (type === 'success') {
        toast.success(String(message), { autoClose: 4000 });
      } else {
        toast.info(String(message), { autoClose: 3000 });
      }
    } catch (e) {
      console[type === 'error' ? 'error' : type === 'warning' ? 'warn' : 'log'](message);
    }
  };

  // Fetch operational academic contexts
  const fetchOperationalContexts = useCallback(async () => {
    if (!tenantId) return;

    setLoadingContexts(true);
    try {
      const response = await api.get('/api/subjects/operational-contexts', {
        params: { tenantId }
      });
      
      if (response.data?.contexts) {
        setAvailableContexts(response.data.contexts);
        
        if (currentAcademicContext && currentAcademicContext.academicYearId) {
          const matchingContext = response.data.contexts.find(ctx => 
            ctx.academicYear._id === currentAcademicContext.academicYearId
          );
          
          if (matchingContext) {
            if (matchingContext.academicYear.isSemesterBased) {
              let semesterId = null;
              let semesterName = null;
              
              if (currentAcademicContext.semesterId && matchingContext.operationalSemesters) {
                const validSemester = matchingContext.operationalSemesters.find(
                  sem => sem._id === currentAcademicContext.semesterId
                );
                if (validSemester) {
                  semesterId = currentAcademicContext.semesterId;
                  semesterName = validSemester.semesterName;
                }
              }

              setAcademicContext({
                academicYearId: matchingContext.academicYear._id,
                academicYearName: matchingContext.academicYear.name,
                isSemesterBased: true,
                semesterId,
                semesterName
              });
            } else {
              let termId = null;
              let termName = null;
              let termNumber = null;
              
              if (currentAcademicContext.termId && matchingContext.operationalTerms) {
                const validTerm = matchingContext.operationalTerms.find(
                  term => term._id === currentAcademicContext.termId
                );
                if (validTerm) {
                  termId = currentAcademicContext.termId;
                  termName = validTerm.termLabel || `Term ${validTerm.termNumber}`;
                  termNumber = validTerm.termNumber;
                }
              }

              setAcademicContext({
                academicYearId: matchingContext.academicYear._id,
                academicYearName: matchingContext.academicYear.name,
                isSemesterBased: false,
                termId,
                termName,
                termNumber
              });
            }
          }
        }
      }
    } catch (err) {
      console.error('Error fetching academic contexts:', err);
      notify('Failed to load academic contexts', 'error');
    } finally {
      setLoadingContexts(false);
    }
  }, [tenantId, currentAcademicContext]);

  useEffect(() => {
    if (isOpen && tenantId) {
      fetchOperationalContexts();
      setUploadResults(null);
      setShowResults(false);
      setFileQueue([]);
      setUploadJobs(new Map());
      setExpandedFiles(new Set());
    }
  }, [isOpen, tenantId, fetchOperationalContexts]);

  useEffect(() => {
    if (!isOpen) {
      setFileQueue([]);
      setUploadJobs(new Map());
      setProgress(0);
      setIsUploading(false);
      setIsProcessing(false);
      setUploadStage('');
      setAcademicContext(null);
      setUploadResults(null);
      setShowResults(false);
      setExpandedFiles(new Set());
    }
  }, [isOpen]);

  const detectFileContext = (fileName, fileContent) => {
    // Try to detect term/semester from filename
    const nameLower = fileName.toLowerCase();
    const termMatch = nameLower.match(/term[_\s]?(\d+)/i);
    const semesterMatch = nameLower.match(/semester[_\s]?(\d+)/i);
    
    if (termMatch && academicContext && !academicContext.isSemesterBased) {
      const termNumber = parseInt(termMatch[1]);
      const availableTerms = getAvailableTerms();
      const matchedTerm = availableTerms.find(t => t.termNumber === termNumber);
      if (matchedTerm) {
        return { termId: matchedTerm._id, termName: matchedTerm.termLabel || `Term ${termNumber}` };
      }
    }
    
    if (semesterMatch && academicContext && academicContext.isSemesterBased) {
      const semesterNumber = parseInt(semesterMatch[1]);
      const availableSemesters = getAvailableSemesters();
      const matchedSemester = availableSemesters.find(s => s.semesterNumber === semesterNumber);
      if (matchedSemester) {
        return { semesterId: matchedSemester._id, semesterName: matchedSemester.semesterName };
      }
    }
    
    return null;
  };

  const parseExcelFileWithContext = (file, detectedContext = null) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          
          const sheetName = workbook.SheetNames.find(name => 
            name.toLowerCase().includes('subject') && 
            !name.toLowerCase().includes('metadata') &&
            !name.toLowerCase().includes('instruction')
          ) || workbook.SheetNames[0];
          
          const sheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(sheet, { header: 1 });
          
          let dataStartRow = -1;
          let headers = [];
          
          for (let i = 0; i < jsonData.length; i++) {
            const row = jsonData[i];
            if (row && row.length > 0) {
              const firstCell = row[0]?.toString().trim().toLowerCase();
              const secondCell = row[1]?.toString().trim().toLowerCase();
              
              if (
                (firstCell && firstCell.includes('subject name')) ||
                (secondCell && secondCell.includes('subject code')) ||
                (firstCell && firstCell.includes('name') && secondCell && secondCell.includes('code'))
              ) {
                dataStartRow = i;
                headers = row;
                break;
              }
            }
          }

          const subjectDataRows = [];
          if (dataStartRow !== -1) {
            for (let i = dataStartRow + 1; i < jsonData.length; i++) {
              const row = jsonData[i];
              if (row && row.length > 0) {
                const firstCell = row[0]?.toString().trim();
                if (firstCell && 
                    !firstCell.includes('BULK SUBJECT') &&
                    !firstCell.includes('Academic Context') &&
                    !firstCell.includes('SECURITY') &&
                    firstCell.length > 0) {
                  
                  const hasRealData = row.some((cell, index) => {
                    if (index >= 2) return false;
                    const cellStr = cell?.toString().trim();
                    return cellStr && cellStr.length > 0;
                  });
                  
                  if (hasRealData) {
                    subjectDataRows.push(row);
                  }
                }
              }
            }
          }

          const isTermBased = headers.some(h => 
            h?.toString().toLowerCase().includes('term')
          );

          resolve({
            fileInfo: {
              name: file.name,
              size: (file.size / 1024 / 1024).toFixed(2) + ' MB',
              lastModified: new Date(file.lastModified).toLocaleDateString(),
              totalSubjects: subjectDataRows.length,
              headers: headers,
              dataStartRow: dataStartRow + 1,
              isTermBased
            },
            previewRows: subjectDataRows.slice(0, 6),
            hasMoreData: subjectDataRows.length > 6,
            sheetName: sheetName,
            detectedContext
          });
        } catch (error) {
          console.error('Error parsing Excel file:', error);
          resolve({ error: 'Failed to parse Excel file. Please ensure you are using the correct template.' });
        }
      };
      reader.readAsArrayBuffer(file);
    });
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    setIsProcessing(true);
    
    const validFiles = [];
    const newFileQueue = [];

    for (const file of files) {
      const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
      if (!['.xlsx', '.xls'].includes(fileExtension)) {
        notify(`${file.name}: Invalid file type. Only .xlsx and .xls allowed.`, 'error');
        continue;
      }

      if (file.size > 10 * 1024 * 1024) {
        notify(`${file.name}: File size must be less than 10MB`, 'error');
        continue;
      }

      const detectedContext = detectFileContext(file.name);
      validFiles.push(file);
      
      const preview = await parseExcelFileWithContext(file, detectedContext);
      
      if (preview.error) {
        notify(`${file.name}: ${preview.error}`, 'error');
        continue;
      }

      const jobId = `${file.name}_${Date.now()}_${Math.random()}`;
      const job = {
        id: jobId,
        file: file,
        name: file.name,
        preview,
        status: 'pending',
        progress: 0,
        error: null,
        results: null,
        detectedContext
      };
      
      newFileQueue.push(job);
      setUploadJobs(prev => new Map(prev).set(jobId, job));
    }

    setFileQueue(prev => [...prev, ...newFileQueue]);
    
    if (validFiles.length > 0) {
      notify(`${validFiles.length} file(s) added to queue. Total subjects: ${newFileQueue.reduce((sum, job) => sum + (job.preview?.fileInfo?.totalSubjects || 0), 0)}`, 'success');
    }
    
    setIsProcessing(false);
  };

  const removeFileFromQueue = (jobId) => {
    setFileQueue(prev => prev.filter(job => job.id !== jobId));
    setUploadJobs(prev => {
      const newMap = new Map(prev);
      newMap.delete(jobId);
      return newMap;
    });
    notify('File removed from queue', 'info');
  };

  const uploadSingleFile = async (job, index) => {
    const jobId = job.id;
    
    setUploadJobs(prev => {
      const updated = new Map(prev);
      updated.set(jobId, { ...job, status: 'uploading' });
      return updated;
    });

    setFileQueue(prev => prev.map(j => 
      j.id === jobId ? { ...j, status: 'uploading' } : j
    ));

    const formData = new FormData();
    formData.append('file', job.file);
    formData.append('tenantId', tenantId);
    formData.append('departmentId', department._id);
    formData.append('departmentName', department.name);
    if (createdBy) formData.append('createdBy', createdBy);
    
    // Add detected context if available
    if (job.detectedContext) {
      if (job.detectedContext.semesterId) {
        formData.append('forceSemesterId', job.detectedContext.semesterId);
      }
      if (job.detectedContext.termId) {
        formData.append('forceTermId', job.detectedContext.termId);
      }
    }

    try {
      const response = await api.post('/api/subjects/bulk-upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadJobs(prev => {
            const updated = new Map(prev);
            updated.set(jobId, { ...updated.get(jobId), progress: percent });
            return updated;
          });
        },
        timeout: 300000
      });

      const { details } = response.data;
      const backendErrors = Array.isArray(details.errors) ? details.errors : [];
      const generalErrors = Array.isArray(details.generalErrors) ? details.generalErrors : [];

      const results = {
        ...details,
        errors: [...backendErrors, ...generalErrors],
        errorCount: backendErrors.length + generalErrors.length,
        skippedCount: Array.isArray(details.skippedDuplicates) ? details.skippedDuplicates.length : 0,
        successCount: details.success || 0
      };

      setUploadJobs(prev => {
        const updated = new Map(prev);
        updated.set(jobId, { ...updated.get(jobId), status: 'completed', results, progress: 100 });
        return updated;
      });

      setFileQueue(prev => prev.map(j => 
        j.id === jobId ? { ...j, status: 'completed', results, progress: 100 } : j
      ));

      return { success: true, results };

    } catch (err) {
      console.error(`Upload error for ${job.name}:`, err);
      
      let errorMessage = err.response?.data?.message || err.message || 'Upload failed';
      let errorDetails = null;

      if (err.response?.data?.details) {
        errorDetails = err.response.data.details;
      }

      setUploadJobs(prev => {
        const updated = new Map(prev);
        updated.set(jobId, { ...updated.get(jobId), status: 'failed', error: errorMessage, errorDetails });
        return updated;
      });

      setFileQueue(prev => prev.map(j => 
        j.id === jobId ? { ...j, status: 'failed', error: errorMessage, errorDetails } : j
      ));

      return { success: false, error: errorMessage, errorDetails };
    }
  };

  const handleUploadAll = async () => {
    if (!validateAcademicContext()) return;
    if (!department || !department._id) {
      notify('Department information is missing.', 'error');
      return;
    }
    if (fileQueue.length === 0) {
      notify('No files to upload', 'error');
      return;
    }

    setIsUploading(true);
    setUploadResults(null);
    setShowResults(false);

    const pendingJobs = fileQueue.filter(job => job.status === 'pending');
    
    // Process in batches of 3 to avoid overwhelming the server
    const batchSize = 3;
    const allResults = [];
    
    for (let i = 0; i < pendingJobs.length; i += batchSize) {
      const batch = pendingJobs.slice(i, i + batchSize);
      const batchPromises = batch.map((job, idx) => uploadSingleFile(job, i + idx));
      const batchResults = await Promise.all(batchPromises);
      allResults.push(...batchResults);
      
      // Update overall progress
      const completedCount = fileQueue.filter(j => j.status === 'completed' || j.status === 'failed').length;
      const totalCount = fileQueue.length;
      setProgress(Math.round((completedCount / totalCount) * 100));
      setUploadStage(`Uploaded ${completedCount} of ${totalCount} files`);
    }

    const successCount = allResults.filter(r => r.success).length;
    const failCount = allResults.filter(r => !r.success).length;
    const totalSubjects = fileQueue.reduce((sum, job) => sum + (job.preview?.fileInfo?.totalSubjects || 0), 0);
    
    const aggregatedResults = {
      totalFiles: fileQueue.length,
      successFiles: successCount,
      failedFiles: failCount,
      totalSubjects,
      files: fileQueue.map(job => ({
        name: job.name,
        status: job.status,
        results: job.results,
        error: job.error,
        errorDetails: job.errorDetails
      }))
    };
    
    setUploadResults(aggregatedResults);
    setShowResults(true);
    
    if (successCount > 0) {
      const contextMsg = academicContext.academicYearName +
        (academicContext.isSemesterBased && academicContext.semesterName ? ` - ${academicContext.semesterName}` : '') +
        (!academicContext.isSemesterBased && academicContext.termName ? ` - ${academicContext.termName}` : '');
      
      if (failCount === 0) {
        notify(`All ${successCount} file(s) uploaded successfully for ${contextMsg} in ${department.name}. Total subjects: ${totalSubjects}`, 'success');
      } else {
        notify(`${successCount} file(s) succeeded, ${failCount} file(s) failed for ${contextMsg} in ${department.name}. Check results for details.`, 'warning');
      }
      
      if (refreshData) refreshData();
    } else {
      notify('All uploads failed. Please check the errors below.', 'error');
    }
    
    setIsUploading(false);
  };

  const handleAcademicContextChange = (field, value) => {
    setAcademicContext(prev => {
      if (field === 'academicYearId') {
        const selectedYear = availableContexts.find(ctx => ctx.academicYear._id === value);
        const isSemesterBased = selectedYear?.academicYear.isSemesterBased || false;
        
        const newContext = {
          academicYearId: value,
          academicYearName: selectedYear?.academicYear.name || '',
          isSemesterBased,
          semesterId: null,
          semesterName: null,
          termId: null,
          termName: null,
          termNumber: null
        };

        if (isSemesterBased && selectedYear?.operationalSemesters?.length === 1) {
          newContext.semesterId = selectedYear.operationalSemesters[0]._id;
          newContext.semesterName = selectedYear.operationalSemesters[0].semesterName;
        } else if (!isSemesterBased && selectedYear?.operationalTerms?.length === 1) {
          newContext.termId = selectedYear.operationalTerms[0]._id;
          newContext.termName = selectedYear.operationalTerms[0].termLabel || 
                               `Term ${selectedYear.operationalTerms[0].termNumber}`;
          newContext.termNumber = selectedYear.operationalTerms[0].termNumber;
        }

        return newContext;
      } else if (field === 'semesterId') {
        const selectedSemester = availableContexts
          .find(ctx => ctx.academicYear._id === prev.academicYearId)
          ?.operationalSemesters.find(sem => sem._id === value);
        
        return {
          ...prev,
          semesterId: value,
          semesterName: selectedSemester?.semesterName || null
        };
      } else if (field === 'termId') {
        const selectedTerm = availableContexts
          .find(ctx => ctx.academicYear._id === prev.academicYearId)
          ?.operationalTerms.find(term => term._id === value);
        
        return {
          ...prev,
          termId: value,
          termName: selectedTerm?.termLabel || `Term ${selectedTerm?.termNumber}`,
          termNumber: selectedTerm?.termNumber || null
        };
      }
      return prev;
    });
  };

  const validateAcademicContext = () => {
    if (!academicContext?.academicYearId) {
      notify('Please select an academic year', 'error');
      return false;
    }

    if (academicContext.isSemesterBased) {
      if (!academicContext.semesterId) {
        notify('Please select a semester for semester-based academic year', 'error');
        return false;
      }
    } else {
      if (!academicContext.termId) {
        notify('Please select a term for term-based academic year', 'error');
        return false;
      }
    }

    return true;
  };

  const handleDownloadTemplate = async () => {
    if (!validateAcademicContext()) return;
    
    setIsDownloading(true);
    try {
      if (!department || !department._id || !department.name) {
        notify('Department information is missing.', 'error');
        setIsDownloading(false);
        return;
      }

      const params = {
        tenantId,
        departmentId: department._id,
        departmentName: encodeURIComponent(department.name),
        academicYearId: academicContext.academicYearId
      };

      if (academicContext.isSemesterBased) {
        if (academicContext.semesterId) {
          params.semesterId = academicContext.semesterId;
        }
      } else {
        if (academicContext.termId) {
          params.termId = academicContext.termId;
        }
      }

      const response = await api.get(`/api/subjects/template/download`, {
        params,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      let filename = `subject_template_${department.name.replace(/\s+/g, '_')}_${
        academicContext.academicYearName.replace(/\s+/g, '_')
      }`;
      
      if (academicContext.isSemesterBased && academicContext.semesterName) {
        filename += `_${academicContext.semesterName.replace(/\s+/g, '_')}`;
      } else if (!academicContext.isSemesterBased && academicContext.termName) {
        filename += `_${academicContext.termName.replace(/\s+/g, '_')}`;
      }
      
      filename += '.xlsx';
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      let contextMsg = academicContext.academicYearName;
      if (academicContext.isSemesterBased && academicContext.semesterName) {
        contextMsg += ` - ${academicContext.semesterName}`;
      } else if (!academicContext.isSemesterBased && academicContext.termName) {
        contextMsg += ` - ${academicContext.termName}`;
      }
      
      notify(`Template downloaded for ${contextMsg}`, 'success');
    } catch (err) {
      console.error('Template download error:', err);
      const errorMsg = err.response?.data?.message || 'Failed to download template';
      notify(errorMsg, 'error');
    } finally {
      setIsDownloading(false);
    }
  };

  const getAvailableSemesters = () => {
    if (!academicContext?.academicYearId) return [];
    const context = availableContexts.find(ctx => ctx.academicYear._id === academicContext.academicYearId);
    return context?.operationalSemesters || [];
  };

  const getAvailableTerms = () => {
    if (!academicContext?.academicYearId) return [];
    const context = availableContexts.find(ctx => ctx.academicYear._id === academicContext.academicYearId);
    return context?.operationalTerms || [];
  };

  const toggleFileExpanded = (fileId) => {
    setExpandedFiles(prev => {
      const newSet = new Set(prev);
      if (newSet.has(fileId)) {
        newSet.delete(fileId);
      } else {
        newSet.add(fileId);
      }
      return newSet;
    });
  };

  const renderFileQueue = () => {
    if (fileQueue.length === 0) return null;

    return (
      <div className="mt-4">
        <h4 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
          <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          File Queue ({fileQueue.length} file(s))
        </h4>
        
        <div className="space-y-3 max-h-80 overflow-y-auto">
          {fileQueue.map((job, idx) => (
            <div key={job.id} className="border border-gray-200 rounded-lg p-3 bg-white hover:shadow-sm transition-shadow">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 flex-1">
                  <div className={`w-2 h-2 rounded-full ${
                    job.status === 'completed' ? 'bg-green-500' :
                    job.status === 'failed' ? 'bg-red-500' :
                    job.status === 'uploading' ? 'bg-blue-500 animate-pulse' :
                    'bg-gray-400'
                  }`}></div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-800 text-sm">{job.name}</span>
                      <span className="text-xs text-gray-500">
                        ({job.preview?.fileInfo?.totalSubjects || 0} subjects)
                      </span>
                      {job.detectedContext && (
                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                          {job.detectedContext.semesterName || job.detectedContext.termName || 'Auto-detected'}
                        </span>
                      )}
                    </div>
                    <div className="mt-1">
                      <div className="w-full bg-gray-200 rounded-full h-1.5">
                        <div
                          className={`h-1.5 rounded-full transition-all duration-300 ${
                            job.status === 'failed' ? 'bg-red-500' :
                            job.status === 'completed' ? 'bg-green-500' :
                            'bg-blue-500'
                          }`}
                          style={{ width: `${job.progress || 0}%` }}
                        ></div>
                      </div>
                    </div>
                    {job.status === 'uploading' && (
                      <p className="text-xs text-blue-600 mt-1">Uploading... {job.progress}%</p>
                    )}
                    {job.status === 'completed' && job.results && (
                      <div className="mt-1 flex gap-3 text-xs">
                        <span className="text-green-600">✓ {job.results.successCount} created</span>
                        {job.results.skippedCount > 0 && (
                          <span className="text-yellow-600">⏭ {job.results.skippedCount} skipped</span>
                        )}
                        {job.results.errorCount > 0 && (
                          <span className="text-red-600">⚠ {job.results.errorCount} errors</span>
                        )}
                      </div>
                    )}
                    {job.status === 'failed' && (
                      <p className="text-xs text-red-600 mt-1">Error: {job.error}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {job.status === 'pending' && !isUploading && (
                    <button
                      onClick={() => removeFileFromQueue(job.id)}
                      className="text-red-500 hover:text-red-700 p-1"
                      title="Remove file"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  )}
                  {job.results && (job.results.errors?.length > 0 || job.results.errorDetails) && (
                    <button
                      onClick={() => toggleFileExpanded(job.id)}
                      className="text-gray-500 hover:text-gray-700 p-1"
                    >
                      <svg className={`w-4 h-4 transition-transform ${expandedFiles.has(job.id) ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
              
              {expandedFiles.has(job.id) && job.results && (job.results.errors?.length > 0 || job.results.errorDetails) && (
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <p className="text-xs font-semibold text-red-700 mb-2">Error Details:</p>
                  <div className="max-h-32 overflow-y-auto bg-red-50 rounded p-2">
                    {job.results.errors?.slice(0, 10).map((error, idx) => (
                      <div key={idx} className="text-xs text-red-600 py-1 border-b border-red-100 last:border-b-0">
                        {error}
                      </div>
                    ))}
                    {job.results.errorDetails?.message && (
                      <div className="text-xs text-red-600 py-1">{job.results.errorDetails.message}</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderFilePreview = () => {
    if (fileQueue.length === 0) return null;
    
    const selectedJob = fileQueue[0];
    if (!selectedJob?.preview || selectedJob.preview.error) return null;

    return (
      <div className="mt-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
        <div className="flex justify-between items-center mb-3">
          <h4 className="text-sm font-semibold text-gray-800">Preview (First File)</h4>
          <span className="text-xs text-gray-500">{fileQueue.length} file(s) in queue</span>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-4 text-xs">
          <div className="space-y-1">
            <div className="flex justify-between">
              <span className="text-gray-600">File Name:</span>
              <span className="font-medium">{selectedJob.preview.fileInfo.name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">File Size:</span>
              <span className="font-medium">{selectedJob.preview.fileInfo.size}</span>
            </div>
          </div>
          <div className="space-y-1">
            <div className="flex justify-between">
              <span className="text-gray-600">Subjects Found:</span>
              <span className="font-medium">{selectedJob.preview.fileInfo.totalSubjects}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Template Type:</span>
              <span className="font-medium">
                {selectedJob.preview.fileInfo.isTermBased ? 'Term-based' : 'Semester-based'}
              </span>
            </div>
          </div>
        </div>

        {selectedJob.preview.fileInfo.totalSubjects > 0 ? (
          <div className="border border-gray-200 rounded overflow-hidden">
            <div className="bg-gray-100 px-3 py-2 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h5 className="text-xs font-semibold text-gray-700">Preview of Subject Data</h5>
                {selectedJob.preview.hasMoreData && (
                  <span className="text-xs text-gray-500">
                    Showing 6 of {selectedJob.preview.fileInfo.totalSubjects} subjects
                  </span>
                )}
              </div>
            </div>
            <div className="max-h-48 overflow-y-auto">
              <table className="min-w-full text-xs">
                <thead className="bg-gray-50">
                  <tr>
                    {selectedJob.preview.fileInfo.headers.slice(0, 5).map((header, index) => (
                      <th key={index} className="px-3 py-2 text-left font-semibold text-gray-700 border-b border-gray-200">
                        {header || `Column ${index + 1}`}
                      </th>
                    ))}
                    {selectedJob.preview.fileInfo.headers.length > 5 && (
                      <th className="px-3 py-2 text-left font-semibold text-gray-700 border-b border-gray-200">...</th>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {selectedJob.preview.previewRows.map((row, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      {row.slice(0, 5).map((cell, cellIndex) => (
                        <td key={cellIndex} className="px-3 py-2 truncate max-w-xs text-gray-600">
                          {cell || '-'}
                        </td>
                      ))}
                      {row.length > 5 && <td className="px-3 py-2 text-gray-400">...</td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="p-4 text-center border border-yellow-200 bg-yellow-50 rounded">
            <p className="text-sm text-yellow-700">No subject data found in the file</p>
          </div>
        )}
      </div>
    );
  };

  const renderUploadResults = () => {
    if (!showResults || !uploadResults) return null;

    return (
      <div className="mt-4 p-4 border border-gray-300 rounded-lg bg-white">
        <h4 className="text-sm font-semibold text-gray-800 mb-3">Upload Summary</h4>
        
        <div className="grid grid-cols-3 gap-4 mb-4">
          <div className="text-center p-3 bg-green-50 border border-green-200 rounded-lg">
            <div className="text-2xl font-bold text-green-600">{uploadResults.successFiles}</div>
            <div className="text-xs text-green-700 font-medium">Files Uploaded</div>
          </div>
          <div className="text-center p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="text-2xl font-bold text-blue-600">{uploadResults.totalSubjects}</div>
            <div className="text-xs text-blue-700 font-medium">Total Subjects</div>
          </div>
          <div className="text-center p-3 bg-red-50 border border-red-200 rounded-lg">
            <div className="text-2xl font-bold text-red-600">{uploadResults.failedFiles}</div>
            <div className="text-xs text-red-700 font-medium">Failed Files</div>
          </div>
        </div>

        {uploadResults.failedFiles > 0 && (
          <div className="mt-3">
            <h5 className="text-sm font-semibold text-red-700 mb-2">Failed Files</h5>
            <div className="max-h-40 overflow-y-auto space-y-2">
              {uploadResults.files.filter(f => f.status === 'failed').map((file, idx) => (
                <div key={idx} className="bg-red-50 border border-red-200 rounded p-2">
                  <p className="text-xs font-medium text-red-800">{file.name}</p>
                  <p className="text-xs text-red-600 mt-1">{file.error}</p>
                  {file.errorDetails?.message && (
                    <p className="text-xs text-red-500 mt-1">{file.errorDetails.message}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-xl shadow-2xl p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        
        <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200">
          <div>
            <h3 className="text-xl font-bold text-gray-900">Bulk Subject Upload</h3>
            <p className="text-sm text-gray-600 mt-1">
              Upload multiple files at once (one per term/semester)
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-2 rounded-lg hover:bg-gray-100"
            disabled={isUploading}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        {department && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-blue-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              <div>
                <p className="text-sm font-semibold text-blue-800">{department.name}</p>
                <p className="text-xs text-blue-600 mt-1">All subjects will be created under this department</p>
              </div>
            </div>
          </div>
        )}

        {/* Step 1: Download Template */}
        <div className="mb-8">
          <div className="flex items-center mb-4">
            <div className="flex-shrink-0 w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
              <span className="text-white text-sm font-bold">1</span>
            </div>
            <h4 className="ml-3 text-lg font-semibold text-gray-900">Get Template</h4>
          </div>

          <div className="ml-11 space-y-4">
            {loadingContexts ? (
              <div className="flex items-center justify-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                <span className="ml-2 text-sm text-gray-600">Loading academic contexts...</span>
              </div>
            ) : availableContexts.length === 0 ? (
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">No active academic years found. Please configure academic years first.</p>
              </div>
            ) : (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Academic Year *</label>
                  <select
                    value={academicContext?.academicYearId || ''}
                    onChange={(e) => handleAcademicContextChange('academicYearId', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg py-3 px-4 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none transition-colors"
                    disabled={isUploading || isDownloading}
                  >
                    <option value="">Select Academic Year</option>
                    {availableContexts.map(context => (
                      <option key={context.academicYear._id} value={context.academicYear._id}>
                        {context.academicYear.name} ({context.academicYear.isSemesterBased ? 'Semester-based' : 'Term-based'})
                      </option>
                    ))}
                  </select>
                </div>

                {academicContext?.isSemesterBased && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Semester *</label>
                    <select
                      value={academicContext?.semesterId || ''}
                      onChange={(e) => handleAcademicContextChange('semesterId', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg py-3 px-4 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none transition-colors"
                      disabled={isUploading || isDownloading}
                    >
                      <option value="">Select Semester</option>
                      {getAvailableSemesters().map(semester => (
                        <option key={semester._id} value={semester._id}>
                          {semester.semesterName} (Semester {semester.semesterNumber})
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {academicContext && !academicContext.isSemesterBased && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Term *</label>
                    <select
                      value={academicContext?.termId || ''}
                      onChange={(e) => handleAcademicContextChange('termId', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg py-3 px-4 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none transition-colors"
                      disabled={isUploading || isDownloading}
                    >
                      <option value="">Select Term</option>
                      {getAvailableTerms().map(term => (
                        <option key={term._id} value={term._id}>
                          {term.termLabel || `Term ${term.termNumber}`}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <button
                  onClick={handleDownloadTemplate}
                  className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 transition-colors font-semibold shadow-sm"
                  disabled={
                    isUploading || 
                    isDownloading || 
                    !department || 
                    !academicContext?.academicYearId || 
                    (academicContext?.isSemesterBased && !academicContext.semesterId) ||
                    (!academicContext?.isSemesterBased && !academicContext.termId)
                  }
                >
                  {isDownloading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      Preparing Template...
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Download Excel Template
                    </>
                  )}
                </button>

                {academicContext?.academicYearId && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-2">
                    <div className="flex items-center gap-2 text-blue-700 text-sm">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>
                        Template will be locked to:{' '}
                        <strong>
                          {academicContext.academicYearName}
                          {academicContext.isSemesterBased && academicContext.semesterName && ` - ${academicContext.semesterName}`}
                          {!academicContext.isSemesterBased && academicContext.termName && ` - ${academicContext.termName}`}
                        </strong>
                      </span>
                    </div>
                    <p className="text-xs text-blue-600 mt-2">Files with term/semester in filename will be auto-detected</p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Step 2: Upload Filled Templates */}
        <div className="mb-6">
          <div className="flex items-center mb-4">
            <div className="flex-shrink-0 w-8 h-8 bg-green-600 rounded-full flex items-center justify-center">
              <span className="text-white text-sm font-bold">2</span>
            </div>
            <h4 className="ml-3 text-lg font-semibold text-gray-900">Upload Subjects</h4>
          </div>

          <div className="ml-11">
            <div className="mb-4">
              <input
                type="file"
                accept=".xlsx,.xls"
                multiple
                id="bulkSubjectInput"
                className="hidden"
                onChange={handleFileSelect}
                disabled={isUploading}
              />
              <label 
                htmlFor="bulkSubjectInput" 
                className={`cursor-pointer p-8 rounded-xl block text-center border-2 border-dashed transition-all ${
                  fileQueue.length > 0 
                    ? 'bg-green-50 border-green-300 text-green-700' 
                    : 'bg-gray-50 border-gray-300 text-gray-700 hover:bg-gray-100 hover:border-gray-400'
                } ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {isProcessing ? (
                  <div className="flex items-center justify-center gap-3">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600"></div>
                    <span className="font-semibold">Processing files...</span>
                  </div>
                ) : fileQueue.length > 0 ? (
                  <div>
                    <svg className="w-12 h-12 mx-auto mb-3 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-base font-semibold">{fileQueue.length} file(s) selected</p>
                    <p className="text-sm text-green-600 mt-2">Click to add more files</p>
                  </div>
                ) : (
                  <div>
                    <svg className="w-12 h-12 mx-auto mb-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    <p className="text-base font-semibold">Choose Excel Files</p>
                    <p className="text-sm text-gray-500 mt-2">Select multiple files at once (.xlsx, .xls, max 10MB each)</p>
                    <p className="text-xs text-gray-400 mt-1">Files with term/semester in name will be auto-detected</p>
                  </div>
                )}
              </label>
            </div>

            {renderFileQueue()}
            {renderFilePreview()}

            {(isUploading || isProcessing) && (
              <div className="mt-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                <div className="flex justify-between text-sm font-medium text-gray-700 mb-2">
                  <span>{uploadStage}</span>
                  <span>{progress}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div className="bg-green-500 h-2.5 rounded-full transition-all duration-300" style={{ width: `${progress}%` }}></div>
                </div>
              </div>
            )}

            {renderUploadResults()}
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-6 py-2.5 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50 transition-colors font-semibold"
            disabled={isUploading}
          >
            {uploadResults ? 'Close' : 'Cancel'}
          </button>
          <button
            onClick={handleUploadAll}
            className="px-6 py-2.5 rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors font-semibold shadow-sm"
            disabled={fileQueue.length === 0 || isUploading || isProcessing || !department || fileQueue.every(j => j.status !== 'pending')}
          >
            {isUploading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Uploading...
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                Upload All ({fileQueue.filter(j => j.status === 'pending').length} files)
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkUploadModal;